import { motion } from 'framer-motion';
import { 
  ClipboardList, 
  Target, 
  TrendingUp, 
  Users, 
  Lightbulb, 
  Shield,
  ArrowRight,
  Phone,
  CheckCircle2,
  BarChart3,
  FileText,
  LineChart,
  PieChart,
  Award
} from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';

const serviceBenefits = [
  {
    icon: Target,
    title: 'Validasi Ide & Model Bisnis',
    description: 'Mengukur kelayakan strategi dan asumsi dasar bisnis Anda melalui analisis yang komprehensif.'
  },
  {
    icon: Lightbulb,
    title: 'Identifikasi Gap & Peluang',
    description: 'Menemukan area perbaikan, efisiensi, dan inovasi baru untuk pertumbuhan berkelanjutan.'
  },
  {
    icon: TrendingUp,
    title: 'Optimasi Strategi Pertumbuhan',
    description: 'Merancang arah pengembangan jangka menengah dan panjang yang lebih terukur.'
  },
  {
    icon: BarChart3,
    title: 'Dukungan Pengambilan Keputusan',
    description: 'Analisis finansial dan risiko mendalam untuk mendukung ekspansi atau pivot strategi.'
  }
];

const methodologySteps = [
  {
    number: '01',
    title: 'Discovery & Assessment',
    description: 'Memahami konteks bisnis Anda melalui wawancara, analisis dokumen internal, dan observasi model bisnis.',
    outputs: ['Business Context Summary', 'Problem & Objective Definition', 'Benchmarking Data Review']
  },
  {
    number: '02',
    title: 'Validation & Analytical',
    description: 'Analisis kuantitatif dan kualitatif terhadap data pasar, keuangan, dan operasional.',
    outputs: ['Market Feasibility & Sensitivity Analysis', 'SWOT, PESTLE, dan Value Chain Analysis', 'Financial & Operational Validation']
  },
  {
    number: '03',
    title: 'Strategic Design',
    description: 'Menyusun strategic blueprint dengan prinsip "insight-to-action" yang mudah diterapkan.',
    outputs: ['Business Model Optimization Framework', 'Strategic Roadmap & Implementation Milestone', 'Financial Projection & Investment Plan']
  }
];

const packages = [
  {
    name: 'Basic Business Plan',
    description: 'Kerangka dasar perencanaan bisnis dan analisis model usaha berdasarkan kondisi pasar terkini.',
    features: [
      'Company Profile & Business Concept Overview',
      'Market Overview & Competitive Landscape',
      'SWOT & Business Model Canvas (BMC)',
      'Marketing Strategy Framework',
      'Business Development Roadmap',
      'Free Strategic Discussion Session'
    ],
    icon: FileText,
    popular: false
  },
  {
    name: 'Premium Business Plan',
    description: 'Pendekatan komprehensif dengan integrasi aspek operasional, SDM, dan keuangan.',
    features: [
      'Semua fitur Basic Plan',
      'Operational & Human Resource Planning',
      'Organizational Workflow & KPI Framework',
      'Basic Financial Projection (P&L, Cash Flow)',
      'Funding & Capital Structure Strategy',
      'Financial Feasibility & Investment Analysis'
    ],
    icon: LineChart,
    popular: true
  },
  {
    name: 'In-Depth Business Plan',
    description: 'Perencanaan bisnis tingkat lanjut untuk ekspansi, M&A, joint venture, atau go-to-market plan.',
    features: [
      'Semua fitur Premium Plan',
      'Advanced Financial Projection & Sensitivity Analysis',
      'In-depth Financial Feasibility & Valuation Insight',
      'Business Risk Assessment & Mitigation Framework',
      'Strategic Scenario Planning',
      'Contingency Strategy'
    ],
    icon: PieChart,
    popular: false
  }
];

const whyChooseUs = [
  {
    icon: Target,
    title: 'Pendekatan Berbasis Data',
    description: 'Insight dan metodologi konsultatif yang terukur dan teruji'
  },
  {
    icon: Users,
    title: 'Tim Berpengalaman',
    description: 'Analis multi-industry dengan pengalaman lapangan yang solid'
  },
  {
    icon: Award,
    title: 'Dokumen Investor-Ready',
    description: 'Hasil akhir siap dipresentasikan ke investor atau manajemen eksekutif'
  },
  {
    icon: Shield,
    title: 'Kerahasiaan Terjamin',
    description: 'NDA (Non-Disclosure Agreement) sebelum riset dimulai'
  }
];

export function Products() {
  const { ref: heroRef, isInView: heroInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: benefitsRef, isInView: benefitsInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: methodologyRef, isInView: methodologyInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: packagesRef, isInView: packagesInView } = useScrollAnimation({ threshold: 0.05 });
  const { ref: whyRef, isInView: whyInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: ctaRef, isInView: ctaInView } = useScrollAnimation({ threshold: 0.2 });

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-[80vh] flex items-center justify-center overflow-hidden"
      >
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center"
        >
          <source src="/business-plan-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85" />

        <div className="relative max-w-7xl mx-auto section-padding py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={heroInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6"
              >
                <ClipboardList className="w-4 h-4 text-primary-400" />
                <span className="text-sm font-medium text-white/90">Produk & Layanan</span>
              </motion.div>

              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Business Plan &
                <span className="text-gradient block">Feasibility Study</span>
              </h1>

              <p className="text-lg md:text-xl text-white/70 mb-8 leading-relaxed">
                Validasi & Optimasi Model Bisnis Anda untuk Pertumbuhan yang Berkelanjutan. 
                Kami membantu perusahaan menilai, memvalidasi, dan mengoptimalkan model bisnis 
                agar selaras dengan dinamika pasar dan strategi pertumbuhan jangka panjang.
              </p>

              <div className="flex flex-wrap gap-4">
                <motion.a
                  href={`tel:${companyInfo.phone}`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors duration-300 shadow-lg shadow-primary-500/30"
                >
                  <Phone className="w-5 h-5" />
                  Konsultasi Gratis
                </motion.a>
                <motion.a
                  href="#packages"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-full hover:bg-white/20 transition-colors duration-300 border border-white/20"
                >
                  Lihat Paket
                  <ArrowRight className="w-5 h-5" />
                </motion.a>
              </div>
            </motion.div>

            {/* Right: Visual */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                {/* Main Card */}
                <motion.div
                  whileHover={{ scale: 1.02, rotateY: 5 }}
                  transition={{ duration: 0.3 }}
                  className="relative z-10 glass rounded-3xl p-8 shadow-2xl shadow-primary-500/10"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30">
                      <ClipboardList className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Business Plan</h3>
                      <p className="text-white/60">Professional Services</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-white/80">
                      <CheckCircle2 className="w-5 h-5 text-primary-400" />
                      <span>Market Analysis</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/80">
                      <CheckCircle2 className="w-5 h-5 text-primary-400" />
                      <span>Financial Projection</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/80">
                      <CheckCircle2 className="w-5 h-5 text-primary-400" />
                      <span>Strategic Roadmap</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/80">
                      <CheckCircle2 className="w-5 h-5 text-primary-400" />
                      <span>Risk Assessment</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        ref={benefitsRef}
        className="relative py-24 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-dark-800 via-dark-900 to-dark-800" />

        <div className="relative max-w-7xl mx-auto section-padding">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={benefitsInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6"
            >
              <span className="w-2 h-2 bg-accent-400 rounded-full" />
              <span className="text-sm font-medium text-white/90">Tujuan & Manfaat</span>
            </motion.div>

            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Mengapa Business Plan Penting
              <span className="text-gradient"> untuk Bisnis Anda?</span>
            </h2>

            <p className="text-lg text-white/70">
              Emporium Business Solution mendukung perusahaan dalam memastikan bahwa setiap 
              elemen dalam model bisnis telah tervalidasi dengan baik melalui analisis yang komprehensif.
            </p>
          </motion.div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 40 }}
                  animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="group glass rounded-2xl p-6 border border-white/10 hover:border-primary-500/30 transition-all duration-500"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-primary-900/50 to-primary-800/30 rounded-xl flex items-center justify-center mb-4 group-hover:from-primary-600 group-hover:to-primary-700 transition-all duration-300">
                    <Icon className="w-6 h-6 text-primary-400 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-primary-400 transition-colors duration-300">
                    {benefit.title}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Target Audience */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-16 glass rounded-2xl p-8"
          >
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Cocok untuk:</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                'Perusahaan yang menyusun strategi pertumbuhan atau ekspansi jangka menjang–panjang',
                'Bisnis yang ingin melakukan diversifikasi, merger & akuisisi, atau joint venture',
                'Startup & korporasi yang memerlukan roadmap bisnis strategis dan investor-ready business plan'
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-accent-400 flex-shrink-0 mt-1" />
                  <p className="text-white/80">{item}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Methodology Section */}
      <section
        ref={methodologyRef}
        className="relative py-24 overflow-hidden"
      >
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center"
        >
          <source src="/methodology-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85" />

        <div className="relative max-w-7xl mx-auto section-padding">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={methodologyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={methodologyInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6"
            >
              <span className="w-2 h-2 bg-primary-400 rounded-full" />
              <span className="text-sm font-medium text-white/90">Metodologi</span>
            </motion.div>

            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Cara Kerja Kami
            </h2>
            <p className="text-lg text-white/70">
              Structured. Data-Driven. Action-Oriented.
            </p>
          </motion.div>

          {/* Steps */}
          <div className="space-y-8">
            {methodologySteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={methodologyInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="glass rounded-3xl p-8 hover:border-primary-500/30 transition-all duration-500"
              >
                <div className="grid lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-2 flex items-center gap-4">
                    <span className="text-5xl font-display font-bold text-primary-400">
                      {step.number}
                    </span>
                  </div>
                  <div className="lg:col-span-4">
                    <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>
                    <p className="text-white/70">{step.description}</p>
                  </div>
                  <div className="lg:col-span-6">
                    <div className="bg-white/5 rounded-xl p-6">
                      <p className="text-sm text-accent-400 font-semibold mb-3">Keluaran:</p>
                      <ul className="space-y-2">
                        {step.outputs.map((output, i) => (
                          <li key={i} className="flex items-center gap-2 text-white/80">
                            <CheckCircle2 className="w-4 h-4 text-primary-400" />
                            {output}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section
        ref={packagesRef}
        id="packages"
        className="relative py-24 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-dark-800 via-dark-900 to-dark-800" />

        {/* Decorative Elements */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-40 -left-40 w-96 h-96 bg-gradient-to-br from-primary-600/10 to-primary-800/5 rounded-full blur-3xl"
        />

        <div className="relative max-w-7xl mx-auto section-padding">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={packagesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={packagesInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6"
            >
              <span className="w-2 h-2 bg-accent-400 rounded-full" />
              <span className="text-sm font-medium text-white/90">Paket Layanan</span>
            </motion.div>

            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Pilih Paket yang Sesuai
              <span className="text-gradient"> Kebutuhan Anda</span>
            </h2>

            <p className="text-lg text-white/70">
              Kami menawarkan berbagai paket layanan yang dapat disesuaikan dengan skala dan kompleksitas bisnis Anda.
            </p>
          </motion.div>

          {/* Packages Grid */}
          <div className="grid lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => {
              const Icon = pkg.icon;
              return (
                <motion.div
                  key={pkg.name}
                  initial={{ opacity: 0, y: 40 }}
                  animate={packagesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  whileHover={{ y: -12 }}
                  className={`relative glass rounded-3xl p-8 transition-all duration-500 ${
                    pkg.popular 
                      ? 'border-2 border-primary-500/50 shadow-xl shadow-primary-500/20' 
                      : 'border border-white/10 hover:border-white/30'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-sm font-semibold rounded-full">
                        Paling Populer
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
                      pkg.popular 
                        ? 'bg-gradient-to-br from-primary-600 to-primary-800 shadow-lg shadow-primary-500/30' 
                        : 'bg-white/10'
                    }`}>
                      <Icon className={`w-8 h-8 ${pkg.popular ? 'text-white' : 'text-primary-400'}`} />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{pkg.name}</h3>
                    <p className="text-white/70">{pkg.description}</p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          pkg.popular ? 'text-primary-400' : 'text-white/50'
                        }`} />
                        <span className="text-white/80 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full py-4 rounded-xl font-semibold transition-all duration-300 ${
                      pkg.popular
                        ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-500/30 hover:shadow-xl'
                        : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Pilih Paket
                  </motion.button>
                </motion.div>
              );
            })}
          </div>

          {/* Additional Service */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={packagesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-12 glass rounded-2xl p-8 border border-accent-500/30"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-accent-500 to-accent-700 rounded-2xl flex items-center justify-center shadow-lg shadow-accent-500/30">
                  <Target className="w-8 h-8 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">Business Monthly Evaluation & Coaching</h3>
                <p className="text-white/70">
                  Pendampingan bisnis berkala untuk memastikan strategi tetap relevan, efektif, dan adaptif. 
                  Termasuk evaluasi strategi periodik, coaching session, dan dashboard pemantauan performa bisnis.
                </p>
              </div>
              <motion.a
                href={`tel:${companyInfo.phone}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-shrink-0 px-6 py-3 bg-accent-600 text-white font-semibold rounded-xl hover:bg-accent-700 transition-colors duration-300"
              >
                Hubungi Kami
              </motion.a>
            </div>
          </motion.div>

          {/* Feasibility Study Package */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={packagesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8 glass rounded-2xl p-8"
          >
            <h3 className="text-xl font-bold text-white mb-4">Feasibility Study Package</h3>
            <p className="text-white/70 mb-6">
              Layanan riset kelayakan proyek untuk memastikan setiap investasi dilakukan secara cermat dan terukur. 
              Cocok bagi perusahaan yang berencana melakukan ekspansi, proyek baru, atau investasi besar.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                'PESTLE Analysis & Social Environment Review',
                'Industry Feasibility Matrix & Market Attractiveness Index',
                'Financial Feasibility & ROI Forecast',
                'Risk & Sensitivity Analysis'
              ].map((item, i) => (
                <div key={i} className="bg-white/5 rounded-xl p-4">
                  <CheckCircle2 className="w-5 h-5 text-primary-400 mb-2" />
                  <p className="text-white/80 text-sm">{item}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section
        ref={whyRef}
        className="relative py-24 overflow-hidden"
      >
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center"
        >
          <source src="/why-choose-us-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85" />

        <div className="relative max-w-7xl mx-auto section-padding">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={whyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Mengapa <span className="text-gradient">Emporium Solution</span>?
            </h2>
          </motion.div>

          {/* Cards Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 40 }}
                  animate={whyInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="group glass rounded-2xl p-6 text-center hover:border-primary-500/30 transition-all duration-500"
                >
                  <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-primary-900/50 to-primary-800/30 rounded-2xl flex items-center justify-center group-hover:from-primary-600 group-hover:to-primary-700 transition-all duration-300">
                    <Icon className="w-7 h-7 text-primary-400 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-primary-400 transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-white/70 text-sm">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="relative py-24 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-dark-900 to-dark-800" />

        <div className="relative max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="glass rounded-3xl p-12 text-center border border-primary-500/30"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={ctaInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-10 h-10 text-white" />
            </motion.div>

            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Free Strategic Consultation
            </h2>

            <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
              Kami memahami bahwa setiap bisnis memiliki tantangan dan konteks yang unik. 
              Jadwalkan sesi konsultasi awal gratis untuk mendiskusikan kebutuhan, ruang lingkup, 
              dan pendekatan terbaik bagi perusahaan Anda.
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-10 py-5 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-lg font-semibold rounded-full hover:from-primary-700 hover:to-primary-800 transition-all duration-300 shadow-xl shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Hubungi Kami Sekarang
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
